/**
 * Modal · Importador Excel maestro (Para Plataforma CONTRATACIÓN_*.xlsm)
 *
 * Reutilizable — se abre desde US_BID360 (Tender360). Al terminar con éxito,
 * emite un CustomEvent `master-excel-imported` en window para que otros
 * módulos (Dashboard, OfertasComercial, MERCADO…) se refresquen automáticamente.
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import {
  Loader2, X, Upload, Send, CheckCircle2, AlertTriangle, History, Globe, Trash2, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

const fmtN = (n) => new Intl.NumberFormat("es-ES").format(n || 0);

const SUPERADMIN_EMAIL = "dredondo@valorizasm.com";

export default function MasterExcelImportModal({ open, onClose }) {
  const { user } = useAuth();
  const isSuperAdmin = (user?.email || "").trim().toLowerCase() === SUPERADMIN_EMAIL;
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [forceState, setForceState] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  // Zona peligrosa (solo superadmin): borrado total + recarga desde cero
  const [showDanger, setShowDanger] = useState(false);
  const [wipeConfirmText, setWipeConfirmText] = useState("");
  const [wiping, setWiping] = useState(false);
  const [wipeResult, setWipeResult] = useState(null);
  // Restaurar desde copia de seguridad (recupera ofertas borradas por un reimport)
  const [backups, setBackups] = useState([]);
  const [selBackup, setSelBackup] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);

  const loadHistory = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ofertas-comercial/import-excel/history?limit=10`);
      setHistory(r.data?.items || []);
    } catch (e) {
      toast.error("Histórico: " + (e.response?.data?.detail || e.message));
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setImportResult(null);
    setShowHistory(false);
    setShowDanger(false);
    setWipeConfirmText("");
    setWipeResult(null);
  }, [open]);

  const onUpload = async () => {
    if (!file) { toast.error("Selecciona un archivo .xlsm"); return; }
    setUploading(true);
    setImportResult(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("dry", String(dryRun));
    fd.append("force_state_only", String(forceState));
    try {
      const r = await axios.post(`${API}/ofertas-comercial/import-excel`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setImportResult(r.data);
      const s = r.data?.stats || {};
      toast.success(
        dryRun
          ? `Simulación: ${s.updated} actualizadas, ${s.inserted} nuevas`
          : `Import OK · ${s.updated} actualizadas · ${s.inserted} nuevas · overlay ${r.data?.pipeline_overlay?.overlay_updated || 0}`,
        { duration: 6000 }
      );
      if (showHistory) loadHistory();
      // Broadcast: otros módulos abiertos escuchan y se refrescan
      if (!dryRun) {
        window.dispatchEvent(new CustomEvent("master-excel-imported", { detail: r.data }));
      }
    } catch (e) {
      toast.error("Importación: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const WIPE_PHRASE = "BORRAR Y RECARGAR";
  const onWipe = async () => {
    if (!file) { toast.error("Selecciona primero el Excel maestro"); return; }
    if (wipeConfirmText.trim().toUpperCase() !== WIPE_PHRASE) {
      toast.error(`Escribe exactamente «${WIPE_PHRASE}» para confirmar`); return;
    }
    if (!window.confirm(
      "ÚLTIMA CONFIRMACIÓN\n\nSe BORRARÁ toda la base de datos de ofertas US (se guarda una copia de seguridad automática) y se reconstruirá desde cero con el Excel que has subido. Todos los KPIs se recalcularán.\n\n¿Continuar?"
    )) return;
    setWiping(true);
    setWipeResult(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("confirm", "WIPE-AND-REIMPORT");
    try {
      const r = await axios.post(`${API}/ofertas-comercial/wipe-and-reimport-excel`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setWipeResult(r.data);
      setWipeConfirmText("");
      const s = r.data?.import_summary?.stats || {};
      toast.success(
        `BD recargada · ${fmtN(r.data?.deleted)} refrescadas · ${fmtN(r.data?.preserved)} preservadas · ${fmtN(s.inserted)} importadas (copia: ${r.data?.backup_collection})`,
        { duration: 9000 }
      );
      window.dispatchEvent(new CustomEvent("master-excel-imported", { detail: r.data?.import_summary }));
    } catch (e) {
      toast.error("Borrado + recarga: " + (e.response?.data?.detail || e.message));
    } finally {
      setWiping(false);
    }
  };

  const loadBackups = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ofertas-comercial/backups`);
      setBackups(r.data?.backups || []);
      if (r.data?.backups?.length) setSelBackup(prev => prev || r.data.backups[0].collection);
    } catch (e) { /* noop — solo superadmin, lista opcional */ }
  }, []);

  const onRestore = async () => {
    if (!selBackup) { toast.error("Elige una copia de seguridad"); return; }
    const b = backups.find(x => x.collection === selBackup);
    const missing = b?.missing_in_live ?? 0;
    if (!window.confirm(
      `Se restaurarán las ofertas que faltan de «${selBackup}» (aprox. ${fmtN(missing)}).\n\n` +
      "Solo añade las que ya no existen (por expediente/cliente+objeto). No borra ni duplica nada.\n\n¿Continuar?"
    )) return;
    setRestoring(true);
    setRestoreResult(null);
    const fd = new FormData();
    fd.append("backup_collection", selBackup);
    try {
      const r = await axios.post(`${API}/ofertas-comercial/restore-from-backup`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setRestoreResult(r.data);
      toast.success(
        `Restauradas ${fmtN(r.data?.restored)} ofertas que faltaban (desde ${selBackup})`,
        { duration: 8000 }
      );
      window.dispatchEvent(new CustomEvent("master-excel-imported", { detail: r.data }));
      loadBackups();
    } catch (e) {
      toast.error("Restaurar: " + (e.response?.data?.detail || e.message));
    } finally {
      setRestoring(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="master-excel-import-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-6" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-[#007934] via-emerald-700 to-teal-600 px-5 py-3 flex items-center justify-between text-white rounded-t-2xl">
          <div className="flex items-center gap-2.5">
            <Globe size={20}/>
            <div>
              <h3 className="font-bold text-base">Importar Excel maestro · US_BID360</h3>
              <p className="text-[11px] opacity-90">Cierre CONTRATACIÓN · sincroniza ofertas + Units Economics + overlay Pipeline en toda la plataforma</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="master-excel-close" disabled={uploading}>
            <X size={18}/>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-[11px] text-amber-800" data-testid="master-excel-anio-note">
            <AlertTriangle size={14} className="shrink-0 mt-0.5"/>
            <span>
              Este importador es solo para el <strong>año en curso</strong> (hoja <code className="bg-white/60 px-1 rounded">Market_SU_26</code>) y actualiza el dato vivo.
              Para <strong>2025 o años anteriores</strong>, ciérralo y usa «Importar año anterior» (junto a este botón, o en YTD Board → Comparativa interanual): se guardan aparte para las comparativas.
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">Archivo (.xlsm / .xlsx)</label>
              <input
                type="file"
                accept=".xlsm,.xlsx"
                onChange={e => { setFile(e.target.files?.[0] || null); setImportResult(null); }}
                className="block w-full text-sm border border-gray-200 rounded-lg p-2 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-[#007934]/10 file:text-[#007934] hover:file:bg-[#007934]/20"
                data-testid="master-excel-file-input"
                disabled={uploading}
              />
            </div>
            <button
              onClick={onUpload}
              disabled={uploading || !file}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#007934] text-white rounded-lg text-sm font-semibold hover:bg-[#006027] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors h-fit"
              data-testid="master-excel-submit">
              {uploading ? <><Loader2 size={14} className="animate-spin"/> Procesando…</> : <><Send size={14}/> {dryRun ? "Simular" : "Importar"}</>}
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-xs">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} disabled={uploading} data-testid="master-excel-dry"/>
              <span className="text-gray-700">Simulación (no escribe en BD)</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={forceState} onChange={e => setForceState(e.target.checked)} disabled={uploading} data-testid="master-excel-force"/>
              <span className="text-gray-700">Solo actualizar estado + fecha presentación</span>
            </label>
            <button
              onClick={() => { setShowHistory(s => !s); if (!showHistory) loadHistory(); }}
              className="ml-auto text-indigo-600 hover:underline font-semibold inline-flex items-center gap-1"
              data-testid="master-excel-history-toggle">
              <History size={11}/> {showHistory ? "Ocultar histórico" : "Ver histórico"}
            </button>
          </div>

          {/* Info: qué se actualiza tras el import */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-[11px] text-blue-900">
            <b>📡 Actualización automática:</b> al terminar con éxito se emite <code>master-excel-imported</code> y estos módulos se refrescan solos si están abiertos: <b>OfertasComercial · Dashboard KPIs · MERCADO · Pipeline · US_BID360/Historic</b>. Además hace <b>overlay Pipeline_&_Solved26_Market</b> para actualizar Units Economics (CAPEX, EBITDA, IRR, MOIC, fase adj., winner) de los expedientes ya conocidos.
          </div>

          {/* Resultado */}
          {importResult && (
            <div className={`rounded-lg border p-4 ${importResult.dry ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="master-excel-result">
              <div className="flex items-center gap-2 mb-3">
                {importResult.dry
                  ? <AlertTriangle size={16} className="text-amber-700"/>
                  : <CheckCircle2 size={16} className="text-emerald-700"/>}
                <span className={`font-bold text-sm ${importResult.dry ? "text-amber-900" : "text-emerald-900"}`}>
                  {importResult.dry ? "Simulación completada (no se ha escrito en BD)" : "Importación realizada correctamente"}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <Kpi label="Filas leídas"   value={fmtN(importResult.stats?.total_rows)}/>
                <Kpi label="Actualizadas"   value={fmtN(importResult.stats?.updated)} color="text-emerald-700"/>
                <Kpi label="Nuevas"         value={fmtN(importResult.stats?.inserted)} color="text-indigo-700"/>
                <Kpi label="Overlay UE"     value={fmtN(importResult.pipeline_overlay?.overlay_updated)} color="text-fuchsia-700"/>
                <Kpi label="Errores"        value={fmtN(importResult.stats?.errors)} color={(importResult.stats?.errors || 0) > 0 ? "text-rose-700" : "text-gray-700"}/>
              </div>
              {Object.keys(importResult.by_state || {}).length > 0 && (
                <div className="mt-3">
                  <p className="text-[11px] font-bold text-gray-600 uppercase tracking-wide mb-1">Estados en el Excel</p>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(importResult.by_state).sort((a, b) => b[1] - a[1]).map(([s, n]) => (
                      <span key={s} className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-md px-2 py-0.5 text-[11px]">
                        <span className="font-semibold text-gray-700">{s}</span>
                        <span className="text-gray-500">·</span>
                        <span className="text-gray-900 font-bold">{n}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {/* Iter230 · Feedback usuario (EPREMASA): lotes que ya no están
                  en el Excel nuevo pero siguen PRESENTADA/PUBLICADA/etc en BD.
                  El import solo actualiza por expediente — si un lote se
                  quitó de la hoja, esto lo avisa para revisión manual. */}
              {(importResult.possibly_stale_count || 0) > 0 && (
                <div className="mt-3 bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="master-excel-stale-warning">
                  <p className="text-[11px] font-bold text-rose-800 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <AlertTriangle size={13}/> {importResult.possibly_stale_count} registros ya no aparecen en este Excel · revisar a mano
                  </p>
                  <p className="text-[10px] text-rose-700 mb-2">
                    Siguen en la BD con estado activo (PRESENTADA/PUBLICADA/POR DECIDIR/EN ESTUDIO) pero su expediente no está en la hoja subida. Puede ser que ese lote no se presentó finalmente — este import NO los borra ni los cambia automáticamente.
                  </p>
                  <div className="max-h-40 overflow-y-auto border border-rose-200 rounded bg-white">
                    <table className="w-full text-[11px]">
                      <thead className="bg-rose-100 text-[10px] uppercase text-rose-700 sticky top-0">
                        <tr>
                          <th className="px-2 py-1 text-left">Expediente</th>
                          <th className="px-2 py-1 text-left">Cliente</th>
                          <th className="px-2 py-1 text-left">Objeto</th>
                          <th className="px-2 py-1 text-left">Estado</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-rose-100">
                        {importResult.possibly_stale.map((s, i) => (
                          <tr key={i} data-testid={`master-excel-stale-row-${i}`}>
                            <td className="px-2 py-1 font-mono text-gray-700">{s.expediente || "—"}</td>
                            <td className="px-2 py-1 text-gray-700">{s.cliente || "—"}</td>
                            <td className="px-2 py-1 text-gray-600 max-w-[220px] truncate" title={s.objeto_contrato}>{s.objeto_contrato || "—"}</td>
                            <td className="px-2 py-1 font-semibold text-rose-700">{s.estado_licitacion}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* SU04 (H06) · lotes del mismo expediente con cliente+objeto+
                  fecha idénticos en la MISMA subida — el índice único los
                  fundiría si se insertaran ambos; se reportan aparte sin
                  perder silenciosamente ninguno. */}
              {(importResult.posibles_lotes_indistinguibles_count || 0) > 0 && (
                <div className="mt-3 bg-orange-50 border border-orange-200 rounded-lg p-3" data-testid="master-excel-lotes-warning">
                  <p className="text-[11px] font-bold text-orange-800 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <AlertTriangle size={13}/> {importResult.posibles_lotes_indistinguibles_count} filas con posible lote indistinguible · NO importadas
                  </p>
                  <p className="text-[10px] text-orange-700 mb-2">
                    Mismo expediente+cliente+objeto+fecha que otra fila de esta subida. Si son lotes distintos, el Excel no los diferencia por texto — fusionarlos sería inventar un dato. Añade el número de lote al texto del objeto en el Excel y vuelve a subir esas filas.
                  </p>
                  <div className="max-h-40 overflow-y-auto border border-orange-200 rounded bg-white">
                    <table className="w-full text-[11px]">
                      <thead className="bg-orange-100 text-[10px] uppercase text-orange-700 sticky top-0">
                        <tr>
                          <th className="px-2 py-1 text-left">Fila</th>
                          <th className="px-2 py-1 text-left">Expediente</th>
                          <th className="px-2 py-1 text-left">Cliente</th>
                          <th className="px-2 py-1 text-left">Objeto</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-orange-100">
                        {importResult.posibles_lotes_indistinguibles.map((s, i) => (
                          <tr key={i} data-testid={`master-excel-lote-row-${i}`}>
                            <td className="px-2 py-1 font-mono text-gray-700">{s.row}</td>
                            <td className="px-2 py-1 font-mono text-gray-700">{s.expediente || "—"}</td>
                            <td className="px-2 py-1 text-gray-700">{s.cliente || "—"}</td>
                            <td className="px-2 py-1 text-gray-600 max-w-[220px] truncate" title={s.objeto_contrato}>{s.objeto_contrato || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Histórico */}
          {showHistory && (
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="master-excel-history">
              <div className="bg-gray-50 px-3 py-2 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-700">Últimas 10 importaciones</span>
                <button onClick={loadHistory} className="text-[10px] text-indigo-600 hover:underline">Refrescar</button>
              </div>
              {history.length === 0 ? (
                <div className="p-4 text-xs text-gray-500 text-center">Sin registros aún</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-[10px] uppercase text-gray-600">
                    <tr>
                      <th className="px-3 py-1.5 text-left">Fecha</th>
                      <th className="px-3 py-1.5 text-left">Usuario</th>
                      <th className="px-3 py-1.5 text-right">Actualizadas</th>
                      <th className="px-3 py-1.5 text-right">Nuevas</th>
                      <th className="px-3 py-1.5 text-right">Overlay</th>
                      <th className="px-3 py-1.5 text-right">Errores</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {history.map((h, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-3 py-1.5 tabular-nums text-gray-700">{h.at?.substring(0, 16).replace("T", " ")}</td>
                        <td className="px-3 py-1.5 text-gray-700">{h.uploaded_by || "—"}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-emerald-700">{fmtN(h.stats?.updated)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-indigo-700">{fmtN(h.stats?.inserted)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-fuchsia-700">{fmtN(h.pipeline_overlay?.overlay_updated)}</td>
                        <td className={`px-3 py-1.5 text-right tabular-nums ${(h.stats?.errors || 0) > 0 ? "text-rose-700 font-bold" : "text-gray-500"}`}>{fmtN(h.stats?.errors)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ── Zona peligrosa · solo superadmin (dredondo) ──────────────── */}
          {isSuperAdmin && (
            <div className="border-2 border-rose-300 rounded-xl overflow-hidden" data-testid="master-excel-danger-zone">
              <button
                onClick={() => { const n = !showDanger; setShowDanger(n); if (n) loadBackups(); }}
                className="w-full bg-rose-50 hover:bg-rose-100 px-4 py-2.5 flex items-center justify-between text-left transition-colors"
                data-testid="master-excel-danger-toggle">
                <span className="inline-flex items-center gap-2 text-sm font-bold text-rose-800">
                  <ShieldAlert size={16}/> Zona peligrosa · Superadmin
                </span>
                <span className="text-[11px] text-rose-600 font-semibold">{showDanger ? "Ocultar" : "Mostrar"}</span>
              </button>

              {showDanger && (
                <div className="p-4 space-y-3 bg-rose-50/40">
                  <div className="flex items-start gap-2 text-[11px] text-rose-800">
                    <AlertTriangle size={14} className="shrink-0 mt-0.5"/>
                    <span>
                      <strong>Recargar la BD de ofertas US desde el Excel</strong> que has seleccionado arriba.
                      Se hace una <strong>copia de seguridad automática</strong> antes y se recalculan todos los KPIs.
                      Ahora es <strong>no destructivo con los resultados históricos</strong>: se preservan las ofertas
                      <strong> adjudicadas / firmadas / no adjudicadas</strong> y las <strong>creadas a mano</strong> aunque no estén en la hoja
                      (ya no desaparecen, p.ej. Barakaldo). Solo se refrescan las filas vivas del importador.
                      <br/>Los datos de años anteriores (comparativas) se guardan aparte y <strong>no</strong> se tocan.
                    </span>
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-rose-700 uppercase tracking-wide mb-1">
                      Para confirmar, escribe <code className="bg-white px-1 rounded border border-rose-200">{WIPE_PHRASE}</code>
                    </label>
                    <input
                      type="text"
                      value={wipeConfirmText}
                      onChange={e => setWipeConfirmText(e.target.value)}
                      placeholder={WIPE_PHRASE}
                      disabled={wiping}
                      className="w-full text-sm border border-rose-300 rounded-lg p-2 focus:ring-2 focus:ring-rose-400 focus:border-rose-400"
                      data-testid="master-excel-wipe-confirm-input"
                    />
                  </div>

                  <button
                    onClick={onWipe}
                    disabled={wiping || !file || wipeConfirmText.trim().toUpperCase() !== WIPE_PHRASE}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-rose-600 text-white rounded-lg text-sm font-bold hover:bg-rose-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    data-testid="master-excel-wipe-submit">
      {wiping ? <><Loader2 size={14} className="animate-spin"/> Recargando filas vivas…</> : <><Trash2 size={14}/> Recargar filas vivas (preserva adjudicadas)</>}
                  </button>
                  {!file && (
                    <p className="text-[11px] text-rose-600">Selecciona primero el Excel maestro arriba.</p>
                  )}

                  {wipeResult && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="master-excel-wipe-result">
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle2 size={16} className="text-emerald-700"/>
                        <span className="font-bold text-sm text-emerald-900">BD recargada · resultados históricos preservados</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <Kpi label="Refrescadas"     value={fmtN(wipeResult.deleted)} color="text-amber-700"/>
                        <Kpi label="Preservadas"     value={fmtN(wipeResult.preserved)} color="text-emerald-700"/>
                        <Kpi label="Importadas"      value={fmtN(wipeResult.import_summary?.stats?.inserted)} color="text-emerald-700"/>
                        <Kpi label="Errores"         value={fmtN(wipeResult.import_summary?.stats?.errors)} color={(wipeResult.import_summary?.stats?.errors || 0) > 0 ? "text-rose-700" : "text-gray-700"}/>
                      </div>
                      <p className="mt-2 text-[11px] text-emerald-800">
                        Copia de seguridad guardada en <code className="bg-white px-1 rounded border border-emerald-200">{wipeResult.backup_collection}</code> ({fmtN(wipeResult.backup_docs)} registros).
                        {typeof wipeResult.preserved === "number" && <> · Se preservaron <strong>{fmtN(wipeResult.preserved)}</strong> ofertas (adjudicadas/firmadas y creadas a mano) que no se borran.</>}
                      </p>
                    </div>
                  )}

                  {/* ── Restaurar desde copia de seguridad (recuperar borradas) ── */}
                  <div className="border-t border-rose-200 pt-3 mt-1" data-testid="master-excel-restore-zone">
                    <div className="flex items-start gap-2 text-[11px] text-slate-700 mb-2">
                      <History size={14} className="shrink-0 mt-0.5"/>
                      <span>
                        <strong>Recuperar ofertas borradas</strong> de una copia de seguridad. Restaura <strong>solo</strong> las que
                        ya no existen por expediente (no duplica ni pisa lo actual). Útil si un “borrar y recargar” anterior dejó fuera
                        adjudicadas de años previos (p.ej. Barakaldo).
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                      <select
                        value={selBackup}
                        onChange={e => setSelBackup(e.target.value)}
                        disabled={restoring || backups.length === 0}
                        data-testid="master-excel-restore-select"
                        className="flex-1 text-sm border border-slate-300 rounded-lg p-2 bg-white disabled:bg-gray-100"
                      >
                        {backups.length === 0 && <option value="">No hay copias de seguridad</option>}
                        {backups.map(b => (
                          <option key={b.collection} value={b.collection}>
                            {b.collection.replace("ofertas_comercial_backup_", "copia ")} · {fmtN(b.docs)} regs · faltan {fmtN(b.missing_in_live)}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={onRestore}
                        disabled={restoring || !selBackup}
                        data-testid="master-excel-restore-submit"
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-bold hover:bg-black disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      >
                        {restoring ? <><Loader2 size={14} className="animate-spin"/> Restaurando…</> : <><History size={14}/> Restaurar faltantes</>}
                      </button>
                    </div>
                    {restoreResult && (
                      <p className="mt-2 text-[11px] text-emerald-800" data-testid="master-excel-restore-result">
                        <CheckCircle2 size={12} className="inline mr-1 text-emerald-700"/>
                        Restauradas <strong>{fmtN(restoreResult.restored)}</strong> ofertas que faltaban · total vivo ahora {fmtN(restoreResult.live_total)}.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, color = "text-gray-900" }) {
  return (
    <div className="bg-white border border-gray-200 rounded p-2">
      <div className="text-[9px] uppercase font-bold tracking-wider text-gray-500">{label}</div>
      <div className={`text-lg font-black tabular-nums ${color}`}>{value}</div>
    </div>
  );
}
